import { m, AnimatePresence } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
// utils
import { fData } from '@/utils/formatters/format-number';
//
import { ShowIf } from '@/utils/ui/showIf';
// import { fileStatus } from '@/sections/orders/forms/order-upload-files';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { Box, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useRef } from 'react';
import { UploadProps } from '@/types/upload';
import Iconify from '../iconify';
import { varFade } from '../../utils/animate';
import FileThumbnail, { fileData } from '../file-thumbnail';
import PDFViewer from '../preview-documents/preview-documents';

// ----------------------------------------------------------------------

export default function MultiFilePreview({
  thumbnail,
  files,
  onRemove,
  onRemoveWithId,
  sx,
  showPdfPreview = false,
}: UploadProps) {
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const handleRemove = (file: File | string) => {
    if (file && typeof file === 'object' && '_id' in file && onRemoveWithId) {
      onRemoveWithId(file);
    } else if (onRemove) {
      onRemove(file);
    }
  };

  return (
    <Stack spacing={2}>
      <AnimatePresence initial={false}>
        {files?.map((file) => {
          const { key, name = '', size = 0 } = fileData(file);
          const isNotFormatFile = typeof file === 'string';
          const isPDF = file.type === 'application/pdf';

          if (thumbnail) {
            return (
              <Stack
                key={key}
                component={m.div}
                {...varFade().inUp}
                alignItems="center"
                display="inline-flex"
                justifyContent="center"
                sx={{
                  m: 0.45,
                  width: 72,
                  height: 72,
                  borderRadius: 1.25,
                  overflow: 'hidden',
                  position: 'relative',
                  border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                  ...sx,
                }}
              >
                <FileThumbnail
                  tooltip
                  imageView
                  file={file}
                  sx={{ position: 'absolute' }}
                  imgSx={{ position: 'absolute' }}
                />

                {(onRemove || onRemoveWithId) && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(file)}
                    sx={{
                      p: 0.45,
                      top: 4,
                      right: 4,
                      position: 'absolute',
                      color: 'common.white',
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                      },
                    }}
                  >
                    <Iconify icon="mingcute:close-line" width={13} />
                  </IconButton>
                )}
              </Stack>
            );
          }

          return (
            <Stack key={key} spacing={2}>
              <Stack
                component={m.div}
                {...varFade().inUp}
                spacing={2}
                direction="row"
                alignItems="center"
                sx={{
                  my: 0.9,
                  py: 0.9,
                  px: 1.35,
                  borderRadius: 1,
                  border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                  ...sx,
                }}
              >
                <FileThumbnail file={file} />

                <ListItemText
                  primary={isNotFormatFile ? file : name}
                  secondary={isNotFormatFile ? '' : fData(size)}
                  secondaryTypographyProps={{
                    component: 'span',
                    typography: 'caption',
                  }}
                />

                <Stack direction="row" spacing={1} alignItems="center">
                  {showPdfPreview && isPDF && (
                    <PDFViewer
                      file={file.preview}
                      fileName={file.name}
                      fileSize={file.size}
                      validatePDF={false}
                      embedded
                      containerRef={pdfContainerRef}
                    />
                  )}

                  <ShowIf condition={(file as any)?.status === 'complete'}>
                    <CloudDoneIcon color="success" />
                  </ShowIf>
                  <ShowIf condition={(file as any)?.status === 'inProgress'}>
                    <Box sx={{ m: 0, position: 'relative' }}>
                      <CloudUploadIcon color="info" />
                      <ShowIf condition={!!(file as any)?.progress}>
                        <CircularProgress
                          variant="determinate"
                          color="secondary"
                          value={(file as any)?.progress}
                          sx={{
                            cursor: 'pointer',
                            position: 'absolute',
                            top: -8,
                            left: -8,
                            zIndex: 1,
                          }}
                        />
                      </ShowIf>
                    </Box>
                  </ShowIf>
                  <ShowIf condition={!['complete', 'inProgress'].includes((file as any)?.status)}>
                    {(onRemove || onRemoveWithId) && (
                      <IconButton size="small" onClick={() => handleRemove(file)}>
                        <Iconify icon="mingcute:close-line" width={14} />
                      </IconButton>
                    )}
                  </ShowIf>
                </Stack>
              </Stack>

              <Stack
                ref={pdfContainerRef}
                sx={{
                  width: '100%',
                  minHeight: 180,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                }}
              />
            </Stack>
          );
        })}
      </AnimatePresence>
    </Stack>
  );
}
